import React from 'react';
import { Box, Typography, TextField, Button, IconButton, Stack, Select, MenuItem, FormControl, InputLabel, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TimeSelector = ({ time, onChange, label }) => {
  const incrementTime = (minutes) => {
    if (!time) {
      onChange('09:00');
      return;
    }
    const [hours, mins] = time.split(':').map(Number);
    let totalMinutes = hours * 60 + mins + minutes;

    if (totalMinutes >= 24 * 60) totalMinutes = 0;
    if (totalMinutes < 0) totalMinutes = 23 * 60 + 50;

    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    onChange(`${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ minWidth: 46 }}>{label}</Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          backgroundColor: '#fff',
          overflow: 'hidden'
        }}
      >
        <TextField
          value={time || '09:00'}
          onChange={(e) => onChange(e.target.value)}
          type="time"
          size="small"
          sx={{
            width: '130px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
              fontSize: '0.875rem'
            },
            '& input': { padding: '4px 8px', textAlign: 'center' }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => incrementTime(10)} sx={{ p: 0.25 }}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => incrementTime(-10)} sx={{ p: 0.25 }}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const ClinicLocationManager = ({ clinicLocations, onChange, availableLocations, showHours = true }) => {
  const addLocation = () => {
    const newLocation = { 
        location_id: '', 
        address: '', 
        district: '',
        team_id: '', 
        selectedDays: {} 
    };
    onChange([...clinicLocations, newLocation]);
  };

  const removeLocation = (index) => {
    const updated = clinicLocations.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleLocationSelect = (index, locId) => {
    const selectedLoc = availableLocations.find(l => l.id === locId);
    if (!selectedLoc) return;

    const updated = [...clinicLocations];
    updated[index] = {
        ...updated[index],
        location_id: locId,
        address: selectedLoc.address,
        district: selectedLoc.district || '',
        team_id: selectedLoc.team_id
    };
    onChange(updated);
  };

  const updateLocation = (index, field, value) => {
    const updated = [...clinicLocations];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const toggleDay = (locationIndex, day) => {
    const location = clinicLocations[locationIndex];
    if (!location.location_id && availableLocations?.length > 0) {
        // Prevent editing days if no location selected in strict mode
        return;
    }
    const selectedDays = { ...location.selectedDays };

    if (selectedDays[day]) {
      delete selectedDays[day];
    } else {
      selectedDays[day] = { start: '09:00', end: '18:00' };
    }

    updateLocation(locationIndex, 'selectedDays', selectedDays);
  };

  const updateDayTime = (locationIndex, day, timeType, value) => {
    const location = clinicLocations[locationIndex];
    const selectedDays = { ...location.selectedDays };
    selectedDays[day] = { ...selectedDays[day], [timeType]: value };
    updateLocation(locationIndex, 'selectedDays', selectedDays);
  };
  
  const hasAvailableLocations = availableLocations && availableLocations.length > 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
        Clinic Locations
      </Typography>

      {!hasAvailableLocations && (
          <Alert severity="warning" sx={{ mb: 2 }}>
              You are not part of any organization that has set clinic locations. Please ask your administrator to add locations in "Manage Organization", or create a new organization.
          </Alert>
      )}

      {clinicLocations.map((location, locationIndex) => (
        <Box
          key={locationIndex}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: '#fff',
            position: 'relative'
          }}
        >
          {/* Location Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
             {hasAvailableLocations ? (
                 <FormControl fullWidth size="small">
                     <InputLabel>Select Clinic Location</InputLabel>
                     <Select
                        value={location.location_id || ''}
                        label="Select Clinic Location"
                        onChange={(e) => handleLocationSelect(locationIndex, e.target.value)}
                     >
                         {(() => {
                             const usedLocationIds = new Set(
                                 clinicLocations
                                     .filter((_, i) => i !== locationIndex)
                                     .map(l => l.location_id)
                                     .filter(Boolean)
                             );
                             return availableLocations.map((loc) => {
                                 const districtStr = loc.district ? `${loc.district} - ` : '';
                                 const alreadyUsed = usedLocationIds.has(loc.id);
                                 return (
                                     <MenuItem key={loc.id} value={loc.id} disabled={alreadyUsed}>
                                         {loc.team_name} - {districtStr}{loc.address}
                                         {alreadyUsed && (
                                             <Typography
                                                 component="span"
                                                 variant="caption"
                                                 sx={{ ml: 1, color: 'text.disabled', fontStyle: 'italic' }}
                                             >
                                                 (already added)
                                             </Typography>
                                         )}
                                     </MenuItem>
                                 );
                             });
                         })()}
                     </Select>
                 </FormControl>
             ) : (
                <TextField
                fullWidth
                placeholder="Enter clinic address"
                value={location.address}
                disabled={true} // Disable manual entry when strict mode is intended but no locations
                multiline
                minRows={1}
                sx={{ backgroundColor: '#f5f5f5' }}
                />
             )}
            
            <IconButton
              size="small"
              onClick={() => removeLocation(locationIndex)}
              sx={{ color: '#d32f2f' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Profile retains locations; Calendar manages existing working hours. */}
          {showHours && <>
          <Box sx={{ mt: 1,  opacity: (hasAvailableLocations && !location.location_id) ? 0.5 : 1, pointerEvents: (hasAvailableLocations && !location.location_id) ? 'none' : 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
              Select Days & Set Hours
            </Typography>

            <Stack spacing={1.25}>
              {DAYS.map((day) => {
                const isSelected = !!location.selectedDays?.[day];
                const start = location.selectedDays?.[day]?.start || '09:00';
                const end = location.selectedDays?.[day]?.end || '18:00';

                return (
                  <Box
                    key={day}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: isSelected ? '#29C0BB' : '#f5f5f5',
                      color: isSelected ? '#fff' : '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box
                      onClick={() => toggleDay(locationIndex, day)}
                      sx={{
                        cursor: 'pointer',
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: isSelected ? 'rgba(255,255,255,0.16)' : '#e0e0e0',
                        color: isSelected ? '#fff' : '#000',
                        minWidth: 110,
                        textAlign: 'center',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: isSelected ? 'rgba(255,255,255,0.26)' : '#d5d5d5',
                        }
                      }}
                    >
                      {day}
                    </Box>

                    {isSelected ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <TimeSelector
                          label="Start"
                          time={start}
                          onChange={(value) => updateDayTime(locationIndex, day, 'start', value)}
                        />
                        <TimeSelector
                          label="End"
                          time={end}
                          onChange={(value) => updateDayTime(locationIndex, day, 'end', value)}
                        />
                      </Box>
                    ) : <></>}
                  </Box>
                );
              })}
            </Stack>
          </Box>
          </>}
        </Box>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={addLocation}
        disabled={!hasAvailableLocations}
        variant="outlined"
        fullWidth
        sx={{
          borderColor: '#29C0BB',
          color: '#29C0BB',
          '&:hover': {
            borderColor: '#1da8a3',
            backgroundColor: 'rgba(41, 192, 187, 0.04)'
          },
          textTransform: 'none',
          py: 1.5
        }}
      >
        Add Clinic Location
      </Button>
    </Box>
  );
};

export default ClinicLocationManager;